/*
 * In-house product comparison.
 *
 * Replaces the Comparable app. The spec rows are the union of whatever keys
 * the compared products declare in their `custom.specification` metafield,
 * rather than a whitelist curated per category in an app admin — so any
 * product with a spec sheet can be compared against any other in its category.
 *
 * The parsing, the 4-product cap and the same-category rule are ports of the
 * mobile app (src/newgear/utils/specParser.ts, src/store/compareSlice.ts,
 * src/newgear/hooks/useCompareToggle.ts) so web and app behave identically.
 */
(function () {
  'use strict';

  var MAX_COMPARE_ITEMS = 6;
  var STORAGE_KEY = 'bajaao:compare';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------------------------------------------------------------- specs */

  /** Treat "-" and blank as "not specified". */
  function cleanValue(raw) {
    var trimmed = (raw || '').trim();
    return trimmed === '-' ? '' : trimmed;
  }

  function normalise(value) {
    return (value || '').trim().toLowerCase();
  }

  /**
   * Parse a "Key : Value" newline-separated blob into ordered entries.
   * Order is preserved so the table follows the source spec sheet.
   */
  function parseSpecBlob(blob) {
    if (!blob) return [];
    return blob
      .split('\n')
      .map(function (line) { return line.trim(); })
      .filter(function (line) { return line.length > 0; })
      .map(function (line) {
        var idx = line.indexOf(':');
        if (idx === -1) return { key: line.trim(), value: '' };
        return { key: line.slice(0, idx).trim(), value: cleanValue(line.slice(idx + 1)) };
      })
      .filter(function (entry) { return entry.key.length > 0; });
  }

  /**
   * Rows are the ordered union of every key across all products (first-seen
   * order), so nothing a single product declares is hidden. A row `differs`
   * when the products don't all agree — including when one specifies a value
   * and another doesn't.
   */
  function buildComparisonRows(specsPerProduct) {
    var order = [];
    var seen = Object.create(null);

    var maps = specsPerProduct.map(function (entries) {
      var map = Object.create(null);
      entries.forEach(function (entry) {
        if (!(entry.key in map)) map[entry.key] = entry.value;
        if (!(entry.key in seen)) {
          seen[entry.key] = true;
          order.push(entry.key);
        }
      });
      return map;
    });

    return order.map(function (key) {
      var values = maps.map(function (map) { return key in map ? map[key] : ''; });
      var distinct = {};
      values.forEach(function (v) { distinct[normalise(v)] = true; });
      return { key: key, values: values, differs: Object.keys(distinct).length > 1 };
    });
  }

  /* ------------------------------------------------------------ categories */

  // Order matters: on a tie, the first group wins. Kept in sync with
  // src/newgear/utils/compareCategory.ts in the mobile app.
  var GROUPS = [
    { key: 'guitars', label: 'Guitars', patterns: ['guitar', 'bass', 'ukulele', 'banjo', 'mandolin'] },
    { key: 'keyboards', label: 'Keyboards & Pianos', patterns: ['keyboard', 'piano', 'synth', 'midi', 'workstation', 'arranger'] },
    { key: 'drums', label: 'Drums & Percussion', patterns: ['drum', 'percussion', 'cymbal', 'cajon', 'tabla'] },
    { key: 'studio', label: 'Studio & Recording', patterns: ['microphone', 'audio interface', 'studio monitor', 'recording', 'preamp', 'mixer', 'headphone'] },
    { key: 'dj', label: 'DJ Gear', patterns: ['turntable', 'dj controller', 'dj mixer'] },
    { key: 'amps', label: 'Amplifiers', patterns: ['amplifier', 'guitar amp', 'bass amp', 'cabinet'] },
    { key: 'effects', label: 'Effects', patterns: ['effect pedal', 'stompbox', 'multi-effect'] },
    { key: 'wind', label: 'Wind Instruments', patterns: ['saxophone', 'trumpet', 'flute', 'clarinet', 'trombone', 'harmonica'] },
    { key: 'orchestral', label: 'Orchestral Strings', patterns: ['violin', 'cello', 'viola'] }
  ];

  function matchGroup(haystack) {
    for (var i = 0; i < GROUPS.length; i++) {
      for (var j = 0; j < GROUPS[i].patterns.length; j++) {
        if (haystack.indexOf(GROUPS[i].patterns[j]) !== -1) return GROUPS[i].key;
      }
    }
    return null;
  }

  /**
   * `type` is the trustworthy signal and is matched first. Tags are noisy — a
   * drum product can carry a guitar cross-sell tag — so the group with the
   * most matching tags wins rather than the first one seen.
   */
  function getCompareCategory(input) {
    var type = (input.type || '').trim().toLowerCase();
    var fromType = type ? matchGroup(type) : null;
    if (fromType) return fromType;

    var tags = (input.tags || []).map(function (t) { return String(t).toLowerCase(); });
    if (tags.length) {
      var best = null;
      GROUPS.forEach(function (group) {
        var hits = tags.filter(function (tag) {
          return group.patterns.some(function (p) { return tag.indexOf(p) !== -1; });
        }).length;
        if (hits > 0 && (!best || hits > best.hits)) best = { key: group.key, hits: hits };
      });
      if (best) return best.key;
    }
    return type || 'other';
  }

  /** Anything we couldn't classify is allowed through — we don't block on ignorance. */
  function areComparable(a, b) {
    if (!a || !b || a === 'other' || b === 'other') return true;
    return a === b;
  }

  function categoryLabel(key) {
    for (var i = 0; i < GROUPS.length; i++) if (GROUPS[i].key === key) return GROUPS[i].label;
    return key && key !== 'other' ? key : 'a different category';
  }

  /* ----------------------------------------------------------------- state */

  var state = { items: [] };
  var barDismissed = false;

  function readStorage() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.slice(0, MAX_COMPARE_ITEMS) : [];
    } catch (e) {
      return [];
    }
  }

  function writeStorage() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch (e) {
      /* private mode / quota — the tray still works for this page view */
    }
  }

  function isSelected(handle) {
    return state.items.some(function (item) { return item.handle === handle; });
  }

  function removeItem(handle) {
    state.items = state.items.filter(function (item) { return item.handle !== handle; });
    writeStorage();
    render();
  }

  function clearAll() {
    state.items = [];
    writeStorage();
    render();
  }

  function addItem(candidate) {
    barDismissed = false;
    state.items.push({
      handle: candidate.handle,
      id: candidate.id,
      title: candidate.title,
      category: candidate.category,
      addedAt: new Date().toISOString()
    });
    writeStorage();
    render();
  }

  /**
   * Adds/removes a product, enforcing the cap and the same-category rule.
   *
   * Wizzy renders the collection and search grids client-side and exposes no
   * productType or tags, so for those cards the category is resolved from the
   * compare-json payload before the rule is applied - the same deferred
   * classification the mobile app does via ProductTaxonomyService.
   */
  function toggle(candidate) {
    if (!candidate.handle) return;

    if (isSelected(candidate.handle)) {
      removeItem(candidate.handle);
      return;
    }
    if (state.items.length >= MAX_COMPARE_ITEMS) {
      notify('You can compare up to ' + MAX_COMPARE_ITEMS + ' products.');
      syncToggles();
      return;
    }

    if (!candidate.type && candidate.tags.length === 0) {
      fetchProduct(candidate.handle).then(function (data) {
        // Software plugins are never comparable. Liquid cards omit their
        // checkbox, but Wizzy's template can't tell, so drop it here.
        if (data && data.compare_excluded) {
          notify('Software plugins can\'t be compared.');
          hideToggles(candidate.handle);
          return;
        }
        // Cards rendered by Liquid omit the checkbox for a product with no
        // spec sheet, but Wizzy's template has no access to the metafield and
        // shows it on every card. This is where those products are caught, so
        // the tray can't fill up with columns that would all be empty.
        if (!hasSpecification(data)) {
          notify('No specifications to compare for this product.');
          syncToggles();
          return;
        }
        candidate.type = data.type || '';
        candidate.tags = data.tags || [];
        commitToggle(candidate);
      }).catch(function () {
        // A failed lookup classifies as 'other', which never blocks a compare.
        commitToggle(candidate);
      });
      return;
    }

    commitToggle(candidate);
  }

  /** Removes the compare checkbox from every card for this handle. */
  function hideToggles(handle) {
    document.querySelectorAll('[data-bjc-toggle]').forEach(function (label) {
      var labelHandle = label.getAttribute('data-bjc-handle') ||
                        handleFromUrl(label.getAttribute('data-bjc-url'));
      if (labelHandle === handle) label.style.display = 'none';
    });
  }

  /** True when the product, or any of its variants, carries a spec sheet. */
  function hasSpecification(data) {
    if (!data) return false;
    if (parseSpecBlob(data.specification).length > 0) return true;
    return (data.variants || []).some(function (variant) {
      return parseSpecBlob(variant.specification).length > 0;
    });
  }

  function commitToggle(candidate) {
    // Re-checked: the category lookup above is asynchronous, so the tray may
    // have changed while it was in flight.
    if (isSelected(candidate.handle)) { syncToggles(); return; }
    if (state.items.length >= MAX_COMPARE_ITEMS) {
      notify('You can compare up to ' + MAX_COMPARE_ITEMS + ' products.');
      syncToggles();
      return;
    }

    var category = getCompareCategory(candidate);
    candidate.category = category;

    var conflicting = state.items.find(function (item) {
      return !areComparable(item.category, category);
    });

    if (conflicting) {
      syncToggles();
      confirmDialog(
        "Can't compare these together",
        'You’re comparing ' + categoryLabel(conflicting.category) +
          '. Start a new comparison for ' + categoryLabel(category) + ' instead?',
        'Start new',
        function () { clearAll(); addItem(candidate); }
      );
      return;
    }

    addItem(candidate);
  }

  /* -------------------------------------------------------------- fetching */

  var cache = Object.create(null);
  var FETCH_TIMEOUT_MS = 15000;

  /**
   * Deliberately sends no `Accept: application/json` header. Shopify honours
   * that header ahead of `?view=` and serves the built-in product JSON API
   * (`{"product": {...}}`) instead of this theme's template — a different
   * shape, which left the table stuck on "Loading specifications…".
   *
   * The payload is shape-checked here rather than at the call sites so a bad
   * response always becomes a rejected promise, never a mid-render throw.
   */
  function fetchProduct(handle) {
    if (cache[handle]) return cache[handle];

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller
      ? window.setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS)
      : null;

    cache[handle] = fetch(
      ROOT + 'products/' + handle + '?view=compare-json',
      controller ? { signal: controller.signal } : undefined
    )
      .then(function (res) {
        if (!res.ok) throw new Error('compare-json ' + res.status + ' for ' + handle);
        return res.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.variants)) {
          throw new Error('unexpected compare-json payload for ' + handle);
        }
        return data;
      })
      .catch(function (err) {
        delete cache[handle];
        throw err;
      })
      .finally(function () {
        if (timer) window.clearTimeout(timer);
      });

    return cache[handle];
  }

  /* --------------------------------------------------------------- helpers */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function notify(message) {
    var existing = document.querySelector('.bjc-toast');
    if (existing) existing.remove();
    var toast = el('div', 'bjc-toast', message);
    document.body.appendChild(toast);
    window.setTimeout(function () { toast.classList.add('is-visible'); }, 10);
    window.setTimeout(function () {
      toast.classList.remove('is-visible');
      window.setTimeout(function () { toast.remove(); }, 300);
    }, 3000);
  }

  function confirmDialog(title, message, confirmLabel, onConfirm) {
    var overlay = el('div', 'bjc-confirm');
    var box = el('div', 'bjc-confirm__box');
    box.appendChild(el('h3', 'bjc-confirm__title', title));
    box.appendChild(el('p', 'bjc-confirm__text', message));

    var actions = el('div', 'bjc-confirm__actions');
    var cancel = el('button', 'bjc-btn bjc-btn--ghost', 'Cancel');
    var ok = el('button', 'bjc-btn bjc-btn--primary', confirmLabel);
    cancel.type = 'button';
    ok.type = 'button';

    function close() { overlay.remove(); }
    cancel.addEventListener('click', close);
    ok.addEventListener('click', function () { close(); onConfirm(); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });

    actions.appendChild(cancel);
    actions.appendChild(ok);
    box.appendChild(actions);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    ok.focus();
  }

  /* ------------------------------------------------------------ the toggles */

  /** "/products/foo?variant=1" (or an absolute URL) -> "foo". */
  function handleFromUrl(url) {
    if (!url) return '';
    var path = String(url).split('?')[0].split('#')[0];
    var idx = path.indexOf('/products/');
    if (idx === -1) return '';
    return path.slice(idx + '/products/'.length).split('/')[0];
  }

  function candidateFrom(label) {
    var tags = (label.getAttribute('data-bjc-tags') || '').split(',')
      .map(function (t) { return t.trim(); })
      .filter(Boolean);
    return {
      handle: label.getAttribute('data-bjc-handle') ||
              handleFromUrl(label.getAttribute('data-bjc-url')),
      id: label.getAttribute('data-bjc-id'),
      title: label.getAttribute('data-bjc-title'),
      type: label.getAttribute('data-bjc-type') || '',
      tags: tags
    };
  }

  /** Reflect state onto every toggle on the page (cards re-render on filter). */
  function syncToggles() {
    document.querySelectorAll('[data-bjc-toggle]').forEach(function (label) {
      var handle = label.getAttribute('data-bjc-handle') ||
                   handleFromUrl(label.getAttribute('data-bjc-url'));
      var input = label.querySelector('[data-bjc-input]');
      var selected = isSelected(handle);
      if (input) input.checked = selected;
      label.classList.toggle('is-selected', selected);
    });
  }

  document.addEventListener('change', function (e) {
    var input = e.target.closest && e.target.closest('[data-bjc-input]');
    if (!input) return;
    var label = input.closest('[data-bjc-toggle]');
    if (!label) return;
    toggle(candidateFrom(label));
  });

  /* ------------------------------------------------------------ sticky bar */

  function buildBar() {
    var bar = document.getElementById('bjc-bar');
    if (!bar) return;

    bar.innerHTML = '';
    var open = state.items.length > 0 && !barDismissed;
    bar.classList.toggle('is-open', open);
    // Keeps the tray from covering the last row of products.
    document.body.classList.toggle('bjc-bar-open', open);
    buildReopen();
    if (!open) return;

    var actions = el('div', 'bjc-bar__actions');

    var compareBtn = el('button', 'bjc-btn bjc-btn--primary bjc-bar__compare');
    compareBtn.type = 'button';
    compareBtn.appendChild(el('span', null, 'COMPARE'));
    compareBtn.appendChild(el('span', 'bjc-bar__count', String(state.items.length)));
    compareBtn.addEventListener('click', openModal);
    actions.appendChild(compareBtn);

    var secondary = el('div', 'bjc-bar__secondary');

    // Hides the tray without discarding the selection; the trash clears it.
    var hideBtn = el('button', 'bjc-btn bjc-btn--ghost bjc-bar__hide', 'Hide');
    hideBtn.type = 'button';
    hideBtn.setAttribute('aria-label', 'Hide the comparison tray');
    hideBtn.addEventListener('click', function () { barDismissed = true; buildBar(); });

    var clearBtn = el('button', 'bjc-btn bjc-btn--ghost bjc-bar__clear', '');
    clearBtn.type = 'button';
    clearBtn.title = 'Clear all';
    clearBtn.setAttribute('aria-label', 'Clear all products from comparison');
    clearBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>';
    clearBtn.addEventListener('click', clearAll);

    secondary.appendChild(hideBtn);
    secondary.appendChild(clearBtn);
    actions.appendChild(secondary);
    bar.appendChild(actions);

    var list = el('div', 'bjc-bar__items');
    state.items.forEach(function (item) {
      list.appendChild(buildBarItem(item));
    });

    // Empty slots make the 4-product cap legible at a glance.
    for (var i = state.items.length; i < MAX_COMPARE_ITEMS; i++) {
      var slot = el('div', 'bjc-bar__item bjc-bar__item--empty');
      slot.appendChild(el('span', 'bjc-bar__slot', 'Add a product'));
      list.appendChild(slot);
    }

    bar.appendChild(list);
  }

  /**
   * The left-edge handle that brings a hidden tray back. Hiding keeps the
   * selection, so without this the only way back would be to untick and
   * re-tick a product.
   */
  function buildReopen() {
    var button = document.getElementById('bjc-reopen');
    if (!button) return;

    var show = state.items.length > 0 && barDismissed;
    button.hidden = !show;
    if (!show) return;

    var count = button.querySelector('[data-bjc-reopen-count]');
    if (count) count.textContent = String(state.items.length);
    button.setAttribute(
      'aria-label',
      'Show the comparison tray (' + state.items.length +
        (state.items.length === 1 ? ' product' : ' products') + ')'
    );

    if (!button.dataset.bjcBound) {
      button.dataset.bjcBound = 'true';
      button.addEventListener('click', function () {
        barDismissed = false;
        buildBar();
      });
    }
  }

  function buildBarItem(item) {
    var cell = el('div', 'bjc-bar__item');

    var remove = el('button', 'bjc-bar__remove', '\u00d7');
    remove.type = 'button';
    remove.setAttribute('aria-label', 'Remove ' + item.title + ' from comparison');
    remove.addEventListener('click', function () { removeItem(item.handle); });
    cell.appendChild(remove);

    var thumb = el('div', 'bjc-bar__thumb');
    cell.appendChild(thumb);

    var title = el('span', 'bjc-bar__title', item.title);
    title.title = item.title;
    cell.appendChild(title);

    fetchProduct(item.handle).then(function (data) {
      var src = data.image;
      if (!src) return;
      var img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }).catch(function () {
      thumb.classList.add('is-empty');
    });

    return cell;
  }

  /* ----------------------------------------------------------------- modal */

  var showOnlyDiffs = false;
  var selectedVariant = Object.create(null);

  function openModal() {
    var modal = document.getElementById('bjc-modal');
    if (!modal) return;
    modal.classList.add('is-open');
    document.body.classList.add('bjc-noscroll');
    renderModal();
  }

  function closeModal() {
    var modal = document.getElementById('bjc-modal');
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('bjc-noscroll');
  }

  function variantFor(data) {
    var chosen = selectedVariant[data.handle];
    var found = data.variants.filter(function (v) { return String(v.id) === String(chosen); })[0];
    return found || data.variants.filter(function (v) { return String(v.id) === String(data.selected_variant_id); })[0] || data.variants[0];
  }

  var renderToken = 0;

  function spinner(message) {
    var wrap = el('div', 'bjc-loading');
    wrap.appendChild(el('span', 'bjc-spinner'));
    wrap.appendChild(el('p', 'bjc-loading__text', message));
    return wrap;
  }

  function renderModal() {
    var body = document.querySelector('#bjc-modal [data-bjc-modal-body]');
    if (!body) return;

    var count = document.querySelector('#bjc-modal [data-bjc-count]');
    if (count) {
      count.textContent = state.items.length
        ? state.items.length + (state.items.length === 1 ? ' product' : ' products')
        : '';
    }

    if (state.items.length === 0) {
      body.innerHTML = '';
      body.appendChild(el('p', 'bjc-empty', 'No products selected for comparison.'));
      return;
    }

    // Guards against an out-of-order render: removing a product or switching a
    // variant re-enters this while an earlier fetch is still in flight.
    var token = ++renderToken;

    body.innerHTML = '';
    body.appendChild(spinner('Loading specifications…'));

    Promise.all(state.items.map(function (item) {
      return fetchProduct(item.handle).catch(function () { return null; });
    })).then(function (results) {
      if (token !== renderToken) return;

      var columns = [];
      var failed = 0;
      results.forEach(function (data) {
        if (!data) { failed++; return; }
        var variant = variantFor(data);
        columns.push({
          data: data,
          variant: variant,
          specs: parseSpecBlob(variant && variant.specification ? variant.specification : data.specification)
        });
      });

      body.innerHTML = '';

      if (columns.length === 0) {
        body.appendChild(el('p', 'bjc-empty', 'Could not load these products. Please try again.'));
        return;
      }

      var rows = buildComparisonRows(columns.map(function (c) { return c.specs; }));
      body.appendChild(buildTable(columns, rows));

      if (failed > 0) {
        body.appendChild(el('p', 'bjc-note',
          failed + (failed === 1 ? ' product' : ' products') + ' could not be loaded and ' +
          (failed === 1 ? 'is' : 'are') + ' not shown.'));
      }
    }).catch(function () {
      // A throw while building the table must not leave the spinner running.
      if (token !== renderToken) return;
      body.innerHTML = '';
      body.appendChild(el('p', 'bjc-empty', 'Something went wrong building the comparison.'));
    });
  }

  function buildTable(columns, specRows) {
    var table = el('table', 'bjc-table');
    var thead = el('thead');
    var headRow = el('tr');

    // Label column carries the "show only differences" control, as it did in
    // the app this replaces.
    var labelHead = el('th', 'bjc-table__label bjc-table__label--head');
    var hasDiffs = specRows.some(function (r) { return r.differs; });
    if (hasDiffs) {
      var toggleLabel = el('label', 'bjc-diffs');
      var toggleInput = document.createElement('input');
      toggleInput.type = 'checkbox';
      toggleInput.checked = showOnlyDiffs;
      toggleInput.addEventListener('change', function () {
        showOnlyDiffs = toggleInput.checked;
        renderModal();
      });
      toggleLabel.appendChild(toggleInput);
      toggleLabel.appendChild(el('span', null, 'Show only differences'));
      labelHead.appendChild(toggleLabel);
    }
    headRow.appendChild(labelHead);

    columns.forEach(function (col) {
      headRow.appendChild(buildProductHead(col));
    });
    thead.appendChild(headRow);
    table.appendChild(thead);

    var tbody = el('tbody');

    // Shopify-native rows, matching the fields the previous app had enabled.
    appendRow(tbody, 'SKU', columns.map(function (c) { return (c.variant && c.variant.sku) || ''; }));
    appendRow(tbody, 'Vendor', columns.map(function (c) { return c.data.vendor || ''; }));
    appendRow(tbody, 'Product Type', columns.map(function (c) { return c.data.type || ''; }));

    var visible = showOnlyDiffs ? specRows.filter(function (r) { return r.differs; }) : specRows;
    visible.forEach(function (row) {
      appendRow(tbody, row.key, row.values, row.differs);
    });

    if (visible.length === 0) {
      var tr = el('tr');
      var td = el('td', 'bjc-empty');
      td.colSpan = columns.length + 1;
      td.textContent = showOnlyDiffs
        ? 'These products match on every specification.'
        : 'No specifications available.';
      tr.appendChild(td);
      tbody.appendChild(tr);
    }

    table.appendChild(tbody);

    var scroller = el('div', 'bjc-table__scroll');
    scroller.appendChild(table);
    return scroller;
  }

  function appendRow(tbody, label, values, differs) {
    var tr = el('tr', differs ? 'bjc-table__row--differs' : null);
    tr.appendChild(el('th', 'bjc-table__label', label));
    values.forEach(function (value) {
      tr.appendChild(el('td', 'bjc-table__value', value && value.length ? value : '\u2014'));
    });
    tbody.appendChild(tr);
  }

  /*
   * The header cell is a fixed-height flex column so that every column lines
   * up horizontally regardless of how long its title is or whether it has a
   * variant dropdown. Image box and title box are fixed height; the buy block
   * is pushed to the bottom with margin-top:auto. Without this, a one-line
   * title and a three-line title put their prices and buttons at different
   * heights, and single-variant columns (no dropdown) sat higher still.
   */
  function buildProductHead(col) {
    var th = el('th', 'bjc-table__product');
    var data = col.data;

    var remove = el('button', 'bjc-table__remove', '\u00d7');
    remove.type = 'button';
    remove.setAttribute('aria-label', 'Remove ' + data.title + ' from comparison');
    remove.addEventListener('click', function () { removeItem(data.handle); renderModal(); });
    th.appendChild(remove);

    var head = el('div', 'bjc-table__head');

    var link = document.createElement('a');
    link.href = data.url;
    link.className = 'bjc-table__link';

    var figure = el('span', 'bjc-table__figure');
    var image = (col.variant && col.variant.image) || data.image;
    if (image) {
      var img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.className = 'bjc-table__image';
      img.loading = 'lazy';
      figure.appendChild(img);
    }
    link.appendChild(figure);
    link.appendChild(el('span', 'bjc-table__title', data.title));
    head.appendChild(link);

    var price = el('div', 'bjc-table__price');
    price.appendChild(el('span', 'bjc-table__price-current', col.variant ? col.variant.price : data.price));
    var wasPrice = col.variant ? col.variant.compare_at_price : data.compare_at_price;
    if (wasPrice) price.appendChild(el('s', 'bjc-table__price-was', wasPrice));
    head.appendChild(price);

    // Bottom-anchored, so buttons align across columns whether or not a
    // dropdown is present.
    var buy = el('div', 'bjc-table__buy');

    if (data.variants.length > 1) {
      var select = el('select', 'bjc-table__variants');
      data.variants.forEach(function (variant) {
        var option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title + (variant.available ? '' : ' \u2014 sold out');
        if (col.variant && String(variant.id) === String(col.variant.id)) option.selected = true;
        select.appendChild(option);
      });
      select.addEventListener('change', function () {
        selectedVariant[data.handle] = select.value;
        renderModal();
      });
      buy.appendChild(select);
    }

    var add = el('button', 'bjc-btn bjc-btn--cart', 'Add to cart');
    add.type = 'button';
    if (!col.variant || !col.variant.available) {
      add.disabled = true;
      add.textContent = 'Sold out';
    } else {
      add.addEventListener('click', function () { addToCart(col.variant.id, add); });
    }
    buy.appendChild(add);
    head.appendChild(buy);

    th.appendChild(head);
    return th;
  }

  function addToCart(variantId, button) {
    var original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add ' + res.status);
        return res.json();
      })
      .then(function () {
        button.textContent = 'Added ✓';
        // Let the theme's cart UI pick the change up if it listens for it.
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
        window.setTimeout(function () {
          button.textContent = original;
          button.disabled = false;
        }, 2000);
      })
      .catch(function () {
        button.textContent = 'Try again';
        button.disabled = false;
        window.setTimeout(function () { button.textContent = original; }, 2000);
      });
  }

  /* ------------------------------------------------------------------ boot */

  function render() {
    syncToggles();
    buildBar();
    var modal = document.getElementById('bjc-modal');
    if (modal && modal.classList.contains('is-open')) renderModal();
  }

  function init() {
    state.items = readStorage();

    var modal = document.getElementById('bjc-modal');
    if (modal) {
      modal.querySelectorAll('[data-bjc-close]').forEach(function (btn) {
        btn.addEventListener('click', closeModal);
      });
      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeModal();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });

    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Wizzy re-renders the collection and search grids on every filter, sort and
  // page change, so freshly injected cards need their checkbox state restored.
  // Coalesced into one pass per frame: an unthrottled observer fires hundreds
  // of times during a single Wizzy render.
  var syncQueued = false;
  var observer = new MutationObserver(function () {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(function () {
      syncQueued = false;
      syncToggles();
    });
  });
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });
})();
